import type { ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { Landscape } from '@/components/generals/landscape'
import { Reveal } from '@/components/generals/reveal'

interface FeatureCardProps {
    title: string
    description: string
    image: string
    index: number
    onVisible?: (index: number) => void
    children: (active: boolean) => ReactNode
    className?: string
}

/**
 * Tall glass card over a landscape backdrop with a live interface preview.
 * The preview starts animating once the card is properly in view.
 */
export const FeatureCard = ({ title, description, image, index, onVisible, children, className }: FeatureCardProps) => {
    const ref = useRef<HTMLElement>(null)
    const active = useInView(ref, { threshold: 0.35 })

    useEffect(() => {
        if (active) onVisible?.(index)
    }, [active, index, onVisible])

    return (
        <Reveal delay={index * 160} className={cn('h-full', className)}>
            <article
                ref={ref}
                className='grain group relative flex h-full min-h-[580px] flex-col overflow-hidden rounded-[32px] bg-ink transition-transform duration-700 ease-cinema hover:-translate-y-2'
            >
                <Landscape
                    src={image}
                    fade='card'
                    sizes='(min-width: 768px) 33vw, 100vw'
                    imageClassName='group-hover:scale-[1.1] group-hover:brightness-95'
                />
                <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit] transition-shadow duration-700' />

                <div className='relative z-10 flex flex-1 flex-col p-5 sm:p-6'>
                    <div className='flex-1'>
                        {children(active)}
                    </div>

                    <div className='mt-10 px-1'>
                        <h3 className='font-serif text-[1.9rem] italic leading-[1.08] text-cream'>
                            {title}
                        </h3>

                        <p className='mt-3 max-w-[32ch] text-sm leading-relaxed text-cream/60'>
                            {description}
                        </p>
                    </div>
                </div>
            </article>
        </Reveal>
    )
}
